<script setup lang="ts">
import { ref, onErrorCaptured } from 'vue'
import { captureSentryException } from '@/config/sentry'

// Props
interface Props {
  fallbackComponent?: string
  showDetails?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  fallbackComponent: 'default',
  showDetails: false
})

// Events
const emit = defineEmits<{
  error: [error: Error, errorInfo: any]
}>()

// State
const hasError = ref(false)
const error = ref<Error | null>(null)
const errorInfo = ref<any>(null)

// Error capture
onErrorCaptured((err, instance, info) => {
  console.error('Error caught by boundary:', err, info)
  
  // Set error state
  hasError.value = true
  error.value = err
  errorInfo.value = info
  
  // Send to Sentry
  captureSentryException(err, {
    errorBoundary: true,
    componentName: instance?.$options.name || 'Unknown',
    errorInfo: info
  })
  
  // Emit error event
  emit('error', err, info)
  
  // Prevent the error from propagating further
  return false
})

// Recovery function
const recover = () => {
  hasError.value = false
  error.value = null
  errorInfo.value = null
}

// Report issue function
const reportIssue = () => {
  if (error.value) {
    // You could open a feedback modal or redirect to a support page
    console.log('Reporting issue:', error.value.message)
    
    // Example: Open Sentry user feedback
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.showReportDialog({
        title: 'Something went wrong',
        subtitle: 'Our team has been notified. Please let us know what happened.',
        subtitle2: 'If you\'d like to help, tell us what happened below.',
        labelName: 'Name',
        labelEmail: 'Email',
        labelComments: 'What happened?',
        labelSubmit: 'Submit Report',
        errorGeneric: 'An unknown error occurred while submitting your report. Please try again.',
        errorFormEntry: 'Some fields were invalid. Please correct the errors and try again.',
        successMessage: 'Your report has been submitted. Thank you!'
      })
    }
  }
}
</script>

<template>
  <div v-if="!hasError">
    <!-- Normal content when no error -->
    <slot />
  </div>
  
  <!-- Error state -->
  <div v-else class="error-boundary">
    <!-- Default error UI -->
    <div v-if="fallbackComponent === 'default'" class="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div class="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div class="mb-6">
          <i class="fa-solid fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">Oops! Something went wrong</h2>
          <p class="text-gray-600">
            We've encountered an unexpected error. Our team has been notified and is working on a fix.
          </p>
        </div>
        
        <!-- Error details (only in development or when explicitly shown) -->
        <div v-if="showDetails && error" class="mb-6 p-4 bg-red-50 rounded-lg text-left">
          <h3 class="text-sm font-semibold text-red-800 mb-2">Error Details:</h3>
          <p class="text-xs text-red-700 font-mono">{{ error.message }}</p>
          <details v-if="error.stack" class="mt-2">
            <summary class="text-xs text-red-600 cursor-pointer">Stack Trace</summary>
            <pre class="text-xs text-red-600 mt-1 overflow-auto">{{ error.stack }}</pre>
          </details>
        </div>
        
        <!-- Action buttons -->
        <div class="space-y-3">
          <button
            @click="recover"
            class="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <i class="fa-solid fa-refresh mr-2"></i>
            Try Again
          </button>
          
          <button
            @click="reportIssue"
            class="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            <i class="fa-solid fa-bug mr-2"></i>
            Report Issue
          </button>
          
          <button
            @click="$router.push('/')"
            class="w-full text-gray-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            <i class="fa-solid fa-home mr-2"></i>
            Go Home
          </button>
        </div>
      </div>
    </div>
    
    <!-- Minimal error UI -->
    <div v-else-if="fallbackComponent === 'minimal'" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <div class="flex items-center">
        <i class="fa-solid fa-exclamation-triangle text-red-500 mr-3"></i>
        <div class="flex-1">
          <h3 class="text-sm font-semibold text-red-800">Error occurred</h3>
          <p class="text-sm text-red-700">{{ error?.message || 'An unexpected error occurred' }}</p>
        </div>
        <button
          @click="recover"
          class="ml-3 text-red-600 hover:text-red-800 transition-colors"
          title="Retry"
        >
          <i class="fa-solid fa-refresh"></i>
        </button>
      </div>
    </div>
    
    <!-- Custom fallback content -->
    <div v-else>
      <slot name="fallback" :error="error" :recover="recover" :reportIssue="reportIssue" />
    </div>
  </div>
</template>

<style scoped>
.error-boundary {
  /* Any specific styles for error boundary */
}
</style>